import {
  buildModuleCells,
  buildTabCells,
  describeModuleCell,
  describeTabCell,
  nextModuleLevel,
  nextTabLevel,
  type AccessArea,
  type Entitlement,
  type ModuleCatalogueEntry,
  type ModuleCell,
  type ModuleChange,
  type ModuleEntitlement,
  type TabChange,
  type TabEntitlement,
} from "@/lib/access";
import { Badge } from "@/components/ui/badge";
import { TableCell, TableRow } from "@/components/ui/table";

/** What a module cell shows. `—` for no access, never a blank (UX W4). */
const LABEL: Record<string, string> = { none: "—", view: "view", edit: "EDIT" };

/**
 * One person's per-module levels, revealed under their row in the access grid.
 *
 * **Why an expansion and not more columns.** The grid is people × areas
 * because a whole-estate scan is what catches an anomaly. Sixteen more columns
 * would end that scan — the table would scroll sideways past the point where
 * two rows can be compared, which is the only thing the grid is for. Modules
 * are a per-person question ("what exactly does Ioan have?"), so they open
 * per person.
 *
 * **Inherited cells are shown, not hidden.** Most modules have no row of their
 * own; they render at the level their area gives them, visibly marked as
 * inherited. Listing only the overrides would mean the screen could show an
 * empty list for someone with full access, and there would be nowhere to click
 * to create the first override.
 */
export function ModuleAccessRow({
  personEmail,
  areas,
  catalogue,
  moduleEntitlements,
  entitlements,
  tabEntitlements = [],
  columnCount,
  disabled,
  onChange,
  onTabChange,
}: {
  personEmail: string;
  areas: AccessArea[];
  catalogue: ModuleCatalogueEntry[];
  moduleEntitlements: ModuleEntitlement[];
  /** Tab grants. Empty on an API older than the tab grant — no tab cells then. */
  tabEntitlements?: TabEntitlement[];
  entitlements: Entitlement[];
  /** Spans the parent grid so the expansion lines up under its own row. */
  columnCount: number;
  disabled: boolean;
  onChange: (change: ModuleChange) => void;
  onTabChange?: (change: TabChange) => void;
}) {
  const cells = buildModuleCells(
    personEmail,
    areas,
    catalogue,
    moduleEntitlements,
    entitlements,
  );
  const tabCells = buildTabCells(
    personEmail,
    catalogue,
    moduleEntitlements,
    tabEntitlements,
    entitlements,
    areas,
  );

  const byArea = new Map<string, ModuleCell[]>();
  for (const cell of cells) {
    const list = byArea.get(cell.area_key) ?? [];
    list.push(cell);
    byArea.set(cell.area_key, list);
  }

  if (cells.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={columnCount} className="bg-ks-grey-4 text-sm text-ks-grey-1">
          No modules are registered yet, so there is nothing to set per module.
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell colSpan={columnCount} className="bg-ks-grey-4">
        <div className="space-y-4 py-2">
          <p className="text-sm text-ks-grey-1">
            Per-module access for <span className="font-bold">{personEmail}</span>. A module
            set here overrides its area, up or down. Click to cycle: inherit &rarr; view &rarr;
            edit &rarr; closed. A module&apos;s tabs, where it has them, can be set the same
            way beneath it.
          </p>

          {areas.map((area) => {
            const modules = byArea.get(area.area_key) ?? [];
            if (modules.length === 0) return null;
            return (
              <div key={area.area_key}>
                <h3 className="mb-1 text-sm font-bold text-ks-ink">{area.display_name}</h3>
                <ul className="flex flex-wrap gap-2">
                  {modules.map((cell) => (
                    <li key={cell.module_key}>
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() =>
                          onChange({
                            person_email: personEmail,
                            module_key: cell.module_key,
                            level: nextModuleLevel(cell.override),
                          })
                        }
                        // The title carries the CAUSE. The badge can only show
                        // the result, and the result alone does not tell an
                        // admin which control to touch to change it.
                        title={describeModuleCell(cell, areas)}
                        aria-label={`${cell.display_name}: ${describeModuleCell(cell, areas)}. Change.`}
                        className="flex items-center gap-2 rounded border border-ks-grey-3 bg-white px-2 py-1"
                      >
                        <span className="text-sm text-ks-ink">{cell.display_name}</span>
                        <Badge variant={cell.effective === "edit" ? "default" : "secondary"}>
                          {LABEL[cell.effective]}
                        </Badge>
                        {/* An inherited cell is the default and says so
                            quietly; an explicit one is the exception and is
                            the thing a reader is scanning for. */}
                        {cell.source === "module" ? (
                          <span className="text-xs font-bold text-ks-ink">set here</span>
                        ) : (
                          <span className="text-xs text-ks-grey-1">inherited</span>
                        )}
                      </button>
                      {/* Tab cells sit beneath their module: the narrowest
                          grant, same cycle, same plumbing with a tab_key. */}
                      {onTabChange && tabCells.some((t) => t.module_key === cell.module_key) ? (
                        <ul className="mt-1 flex flex-wrap gap-1 pl-3">
                          {tabCells
                            .filter((t) => t.module_key === cell.module_key)
                            .map((tab) => (
                              <li key={tab.tab_key}>
                                <button
                                  type="button"
                                  disabled={disabled}
                                  onClick={() =>
                                    onTabChange({
                                      person_email: personEmail,
                                      module_key: tab.module_key,
                                      tab_key: tab.tab_key,
                                      level: nextTabLevel(tab.override),
                                    })
                                  }
                                  title={describeTabCell(tab)}
                                  aria-label={`${cell.display_name} · ${tab.display_name} tab: ${describeTabCell(tab)}. Change.`}
                                  className="flex items-center gap-1 rounded border border-ks-grey-3 bg-white px-1.5 py-0.5"
                                >
                                  <span className="text-xs text-ks-ink">{tab.display_name}</span>
                                  <Badge variant="outline">{LABEL[tab.effective]}</Badge>
                                  <span
                                    className={
                                      tab.source === "tab"
                                        ? "text-xs font-bold text-ks-ink"
                                        : "text-xs text-ks-grey-1"
                                    }
                                  >
                                    {tab.source !== "tab"
                                      ? "inherited"
                                      : tab.effective === "none"
                                        ? "closed"
                                        : "set here"}
                                  </span>
                                </button>
                              </li>
                            ))}
                        </ul>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </TableCell>
    </TableRow>
  );
}
